import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { resetPassword } from '../../../api/auth';
import { PageMeta } from '../shared/PageMeta';

const schema = z.object({
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
});

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const token = searchParams.get('token') || '';

  const { register, handleSubmit, formState: { errors } } = useForm<{ password: string; confirmPassword: string }>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: { password: string }) => {
    setLoading(true);
    try {
      await resetPassword(token, data.password);
      toast.success('Password reset successfully');
      navigate('/marketplace/login');
    } catch {
      toast.error('Invalid or expired reset link');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <PageMeta title="Reset Password" description="Reset your password" path="/marketplace/reset-password" />
        <h2 className="text-2xl mb-4">Invalid Link</h2>
        <p className="text-spi-slate mb-6">This reset link is invalid or has expired.</p>
        <Link to="/marketplace/forgot-password" className="btn btn-primary">Request New Link</Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-16">
      <PageMeta title="Reset Password" description="Set a new password" path="/marketplace/reset-password" />
      <div className="max-w-md mx-auto card p-8">
        <h2 className="text-2xl mb-2 text-center">Set New Password</h2>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-6">
          <div>
            <label className="input-label">New Password</label>
            <input {...register('password')} type="password" className="input" />
            {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>}
          </div>
          <div>
            <label className="input-label">Confirm Password</label>
            <input {...register('confirmPassword')} type="password" className="input" />
            {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword.message}</p>}
          </div>
          <button type="submit" disabled={loading} className="btn btn-primary w-full">
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>
      </div>
    </div>
  );
}
